import { css } from 'aphrodite';
import * as React from 'react';
import type { ISupportCategory } from '../../../models/Support/ISupportCategory';
import type { ISupportTicket } from '../../../models/Support/ISupportTicket';
import { TicketPriority, TicketStatus } from '../../../models/Support/SupportEnums';
import {
	useAssignTicketMutation,
	useUpdateTicketCategoryMutation,
	useUpdateTicketPriorityMutation,
	useUpdateTicketStatusMutation,
} from '../../../queries/Support/useSupportTicketMutations';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { CategoryPicker } from './CategoryPicker';
import { inboxStyles as s } from './styles/inboxStyles';
import { getStatusInfo } from './supportTicketPresentation';
import { PRIORITY_EDIT_OPTIONS, STATUS_EDIT_OPTIONS } from './TicketTableActions';

interface TicketDetailsEditorProps {
	ticket: ISupportTicket;
	categories: ISupportCategory[];
	teamMembers: { id: string; name: string }[];
	/** Called after the ticket leaves the agent's plate (closed / assigned) — parent advances to the next ticket. */
	onTicketLeftView?: () => void;
}

/**
 * Editable Status / Priority / Assignee / Category controls for the details panel. These moved here
 * from the ticket top bar: the masthead app bar is now a read-only identity strip, and all ticket
 * mutations live alongside the rest of the ticket metadata in the right-hand details pane.
 */
export const TicketDetailsEditor: React.FC<TicketDetailsEditorProps> = ({
	ticket,
	categories,
	teamMembers,
	onTicketLeftView,
}) => {
	const updateStatus = useUpdateTicketStatusMutation(ticket.id);
	const assignTicket = useAssignTicketMutation(ticket.id);
	const updatePriority = useUpdateTicketPriorityMutation(ticket.id);
	const updateCategory = useUpdateTicketCategoryMutation(ticket.id);

	// Support has no MutationCache/toast, so failures must surface inline (matches CategoriesTab).
	const [mutationError, setMutationError] = React.useState<string | null>(null);
	const feedback = (failMessage: string, onSuccess?: () => void) => ({
		onSuccess: () => {
			setMutationError(null);
			onSuccess?.();
		},
		onError: (error: unknown) => setMutationError(getApiErrorMessage(error, failMessage)),
	});

	return (
		<div className={css(s.detailEditRows)}>
			{mutationError && (
				<div className={css(s.detailEditError)} role='alert' aria-live='assertive'>
					{mutationError}
				</div>
			)}
			<div className={css(s.detailEditRow)}>
				<span className={css(s.detailKey)}>Status</span>
				<Select
					value={ticket.status === TicketStatus.New ? TicketStatus.Open : ticket.status}
					onValueChange={value => {
						const option = STATUS_EDIT_OPTIONS.find(item => item.value === value);
						if (!option) return;
						updateStatus.mutate(
							option.value,
							feedback(
								'Failed to update ticket status.',
								option.value === TicketStatus.Closed ? onTicketLeftView : undefined
							)
						);
					}}
				>
					<SelectTrigger aria-label='Ticket status'>
						<SelectValue render={() => <span data-slot='select-value'>{getStatusInfo(ticket.status).label}</span>} />
					</SelectTrigger>
					<SelectContent>
						{STATUS_EDIT_OPTIONS.map(option => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className={css(s.detailEditRow)}>
				<span className={css(s.detailKey)}>Priority</span>
				<Select
					value={ticket.priority || TicketPriority.Normal}
					onValueChange={value => {
						const option = PRIORITY_EDIT_OPTIONS.find(item => item.value === value);
						if (option) updatePriority.mutate(option.value, feedback('Failed to update priority.'));
					}}
				>
					<SelectTrigger aria-label='Ticket priority'>
						<SelectValue
							render={() => <span data-slot='select-value'>{ticket.priority || TicketPriority.Normal}</span>}
						/>
					</SelectTrigger>
					<SelectContent>
						{PRIORITY_EDIT_OPTIONS.map(option => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className={css(s.detailEditRow)}>
				<span className={css(s.detailKey)}>Assignee</span>
				<Select
					value={ticket.assigneeId || ''}
					onValueChange={value =>
						assignTicket.mutate(
							value || null,
							feedback('Failed to assign ticket.', value ? onTicketLeftView : undefined)
						)
					}
				>
					<SelectTrigger aria-label='Ticket assignee'>
						<SelectValue
							placeholder='Unassigned'
							render={() => {
								const match = teamMembers.find(a => a.id === ticket.assigneeId);
								return (
									<span data-slot='select-value'>
										{match?.name ?? (ticket.assigneeId ? ticket.assigneeId : 'Unassigned')}
									</span>
								);
							}}
						/>
					</SelectTrigger>
					<SelectContent>
						<SelectItem key='assignee-unassigned' value=''>
							Unassigned
						</SelectItem>
						{teamMembers.map(agent => (
							<SelectItem key={agent.id} value={agent.id}>
								{agent.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className={css(s.detailEditRow)}>
				<span className={css(s.detailKey)}>Category</span>
				<CategoryPicker
					categories={categories}
					selectedId={ticket.categoryId}
					onSelect={id => updateCategory.mutate(id || null, feedback('Failed to update category.'))}
				/>
			</div>
		</div>
	);
};
